type CallableFailure = Readonly<{ code?: unknown }>;

const transientSessionReadCodes = new Set([
  'functions/internal',
  'functions/unavailable',
  'functions/resource-exhausted',
]);

const sessionReadFailureCode = (error: unknown) =>
  typeof error === 'object' && error !== null
    ? String((error as CallableFailure).code ?? '')
    : '';

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => globalThis.setTimeout(resolve, milliseconds));

/**
 * `loadIdentitySession` is a read-only admission gate. The Functions SDK maps an
 * App Check verifier outage to `functions/unauthenticated`, the same code used
 * for a rejected token. Refresh exactly once and repeat this read once: a stale
 * browser token or a recovered verifier can proceed, while a rejected token and
 * every authority error remain blocked.
 */
export async function retryIdentitySessionRead<T>(
  read: () => Promise<T>,
  refreshAppCheckToken: () => Promise<unknown>,
  delays: readonly number[] = [500, 1_200],
): Promise<T> {
  let retry = 0;
  let appCheckRefreshUsed = false;
  for (;;) {
    try {
      return await read();
    } catch (error) {
      const code = sessionReadFailureCode(error);
      if (code === 'functions/unauthenticated' && !appCheckRefreshUsed) {
        appCheckRefreshUsed = true;
        await refreshAppCheckToken();
      } else {
        const delay = delays[retry];
        if (!transientSessionReadCodes.has(code) || delay === undefined) throw error;
        await wait(delay);
      }
      retry += 1;
    }
  }
}
